/**
 * SupervisionEmptyState
 *
 * Thoughtful empty states for:
 * - No unsigned documentation
 * - No supervisees assigned
 * - No comments or feedback yet
 * - Limited permissions
 * - Partially signed edge cases
 *
 * Informative, reassuring, and actionable (not dead ends).
 */

import { Box, Button, Stack, Text, Title, ThemeIcon } from '@mantine/core';
import {
  IconClipboardCheck,
  IconClipboardList,
  IconFileOff,
  IconLock,
  IconMessageCircleOff,
  IconUserOff,
} from '@tabler/icons-react';

export type SupervisionEmptyVariant =
  | 'no-cosign-notes'
  | 'no-unsigned-notes'
  | 'no-missing-documents'
  | 'no-supervisees'
  | 'no-comments'
  | 'limited-permissions'
  | 'no-results';

interface SupervisionEmptyStateProps {
  variant: SupervisionEmptyVariant;
  onClearFilters?: () => void;
}

export function SupervisionEmptyState({
  variant,
  onClearFilters,
}: SupervisionEmptyStateProps): JSX.Element {
  switch (variant) {
    case 'no-cosign-notes':
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="green" variant="light">
              <IconClipboardCheck size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              All caught up
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              You have no notes awaiting co-sign in your queue. When clinicians submit documents
              for review, they’ll appear here.
            </Text>
          </Stack>
        </Box>
      );

    case 'no-unsigned-notes':
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="green" variant="light">
              <IconClipboardCheck size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              All caught up
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              You have no unsigned notes. Past sessions without a note and draft notes will appear
              here.
            </Text>
          </Stack>
        </Box>
      );

    case 'no-missing-documents':
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="green" variant="light">
              <IconClipboardList size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              All caught up
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              No missing documents. All required documents are present.
            </Text>
          </Stack>
        </Box>
      );

    case 'no-supervisees':
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="gray" variant="light">
              <IconUserOff size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              No supervisees assigned
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              You don’t have any clinicians assigned to you for supervision. Contact your practice
              admin to set up supervisee relationships.
            </Text>
          </Stack>
        </Box>
      );

    case 'no-comments':
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="gray" variant="light">
              <IconMessageCircleOff size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              No comments yet
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              Add a comment to give feedback on this document. Your comments will appear here and
              the clinician will be notified.
            </Text>
          </Stack>
        </Box>
      );

    case 'limited-permissions':
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="orange" variant="light">
              <IconLock size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              Limited access
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              You don’t have permission to view the supervision section. If you believe this is an
              error, contact your practice administrator.
            </Text>
          </Stack>
        </Box>
      );

    case 'no-results':
    default:
      return (
        <Box py="xl" px="md">
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="xl" color="gray" variant="light">
              <IconFileOff size={36} />
            </ThemeIcon>
            <Title order={4} ta="center">
              No documents match your filters
            </Title>
            <Text c="gray.6" size="sm" ta="center" maw={360}>
              Try adjusting or clearing your filters to see more items.
            </Text>
            {onClearFilters && (
              <Button variant="light" color="gold" onClick={onClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
        </Box>
      );
  }
}
